/* global Word */
import type { TemplateProfile } from '../templates';
import type { ElementDefinition } from './types';

const DUMMY_TEXT =
  'Insert your text here. This block will automatically wrap and respect the layout margins as you type or paste new content.';

type LayoutType = 'left' | 'right' | 'full';

function toHex(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

async function insertTextBlock(layoutType: LayoutType, profile: TemplateProfile): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    const para = selection.insertParagraph(DUMMY_TEXT, Word.InsertLocation.after);

    para.font.name = profile.typography.fontFamily;
    para.font.size = profile.typography.graphHintSize;
    para.font.color = toHex(profile.colorPalette.sourceText);
    para.alignment = Word.Alignment.justified;
    para.lineSpacing = 13.8;
    para.spaceAfter = 8;

    switch (layoutType) {
      case 'left':
        para.leftIndent = 0;
        para.rightIndent = 135;
        break;
      case 'right':
        para.leftIndent = 135;
        para.rightIndent = 0;
        break;
      case 'full':
        para.leftIndent = 0;
        para.rightIndent = 0;
        para.spaceBefore = 5.7;
        para.spaceAfter = 0;
        break;
    }

    para.select();
    await context.sync();
  });
}

/** Element definition for left text block insertion. */
export const textBlockLeftElement: ElementDefinition = {
  id: 'text-block-left',
  label: 'Text Left',
  icon: 'TextAlignLeftRegular',
  description: 'Left-column text block',
  category: 'text',
  templateIds: ['base'],
  async insertAsync(profile: TemplateProfile): Promise<void> {
    await insertTextBlock('left', profile);
  },
};

/** Element definition for right text block insertion. */
export const textBlockRightElement: ElementDefinition = {
  id: 'text-block-right',
  label: 'Text Right',
  icon: 'TextAlignRightRegular',
  description: 'Right-column text block',
  category: 'text',
  async insertAsync(profile: TemplateProfile): Promise<void> {
    await insertTextBlock('right', profile);
  },
};

/** Element definition for full text block insertion. */
export const textBlockFullElement: ElementDefinition = {
  id: 'text-block-full',
  label: 'Text Full',
  icon: 'TextColumnOneRegular',
  description: 'Full-width text block',
  category: 'text',
  templateIds: ['base'],
  async insertAsync(profile: TemplateProfile): Promise<void> {
    await insertTextBlock('full', profile);
  },
};
